import React, { useState, useRef } from "react";
import {
  MdFileUpload,
  MdDownload,
  MdCompareArrows,
  MdDelete,
  MdCheckCircle,
  MdFolder,
  MdClose,
} from "react-icons/md";
import { fetchWrapper } from "../utils/fetchWrapper";
import "./ImageConverter.css";

const FORMATS = [
  { value: "webp", label: "WebP" },
  { value: "avif", label: "AVIF" },
];

const ImageConverter = () => {
  const [items, setItems] = useState([]);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState("webp");
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Set webkitdirectory via DOM API since React doesn't pass it reliably
  const setFolderRef = (el) => {
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
    folderInputRef.current = el;
  };

  const addFiles = (fileList) => {
    const imageFiles = Array.from(fileList).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (imageFiles.length === 0) {
      setError("No valid image files found.");
      return;
    }
    const newItems = imageFiles.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      convertedUrl: null,
      convertedSize: null,
      status: "pending",
    }));
    setItems((prev) => [...prev, ...newItems]);
    setError(null);
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setItems([]);
    setError(null);
  };

  const convertAll = async () => {
    if (items.length === 0) return;
    setConverting(true);
    setError(null);

    const updated = [...items];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === "done") continue;

      updated[i].status = "converting";
      setItems([...updated]);

      const formData = new FormData();
      formData.append("image", updated[i].file);

      try {
        const response = await fetchWrapper.post(
          `/image-converter/convert?format=${format}`,
          formData,
          {
            responseType: "blob",
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        const blob = new Blob([response.data]);
        updated[i].convertedUrl = URL.createObjectURL(blob);
        updated[i].convertedSize = blob.size;
        updated[i].status = "done";
      } catch (err) {
        console.error(`Error converting ${updated[i].file.name}:`, err);
        updated[i].status = "error";
      }

      setItems([...updated]);
    }

    setConverting(false);
  };

  const downloadSingle = (item) => {
    if (!item.convertedUrl) return;
    const link = document.createElement("a");
    link.href = item.convertedUrl;
    const nameWithoutExt = item.file.name.split(".").slice(0, -1).join(".");
    link.setAttribute("download", `${nameWithoutExt}.${format}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllAsZip = async () => {
    const pendingFiles = items.filter((it) => it.file);
    if (pendingFiles.length === 0) return;

    const formData = new FormData();
    pendingFiles.forEach((it) => formData.append("images", it.file));

    try {
      const response = await fetchWrapper.post(
        `/image-converter/convert-bulk?format=${format}`,
        formData,
        {
          responseType: "blob",
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `converted-${format}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading ZIP:", err);
      setError("Failed to download ZIP. Please try again.");
    }
  };

  const downloadAll = () => {
    const doneItems = items.filter(
      (it) => it.status === "done" && it.convertedUrl,
    );
    doneItems.forEach((item) => downloadSingle(item));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const hasDone = items.some((it) => it.status === "done");
  const hasPending = items.some(
    (it) => it.status === "pending" || it.status === "error",
  );
  const selectedLabel =
    FORMATS.find((f) => f.value === format)?.label || "WebP";

  return (
    <div className="image-converter-container">
      <div className="converter-header">
        <h1>Image Converter</h1>
        <p>Convert images to optimized WebP or AVIF format.</p>
      </div>

      {/* Format selector */}
      <div className="format-selector">
        <span className="format-label">Output format:</span>
        <div className="format-toggle">
          {FORMATS.map((f) => (
            <button
              key={f.value}
              className={`format-btn ${format === f.value ? "active" : ""}`}
              onClick={() => setFormat(f.value)}
              disabled={converting}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="converter-card">
        {items.length === 0 ? (
          <div
            className="upload-zone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <MdFileUpload className="upload-icon" />
            <h3>Drag & Drop or Click to Upload</h3>
            <p className="text-secondary">
              Supports PNG, JPG, JPEG, and more. Select multiple files or a
              folder.
            </p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept="image/*"
              multiple
            />
            <input
              type="file"
              ref={setFolderRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept="image/*"
              multiple
            />
            <div
              className="upload-buttons"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="btn-upload-files"
                onClick={() => fileInputRef.current.click()}
              >
                <MdFileUpload size={18} /> Select Files
              </button>
              <button
                className="btn-add-folder"
                onClick={() => folderInputRef.current.click()}
              >
                <MdFolder size={16} /> Add Folder
              </button>
            </div>
          </div>
        ) : (
          <div className="multi-preview-area">
            <div className="add-more-bar">
              <div className="add-more-left">
                <span className="file-count">
                  {items.length} image{items.length !== 1 ? "s" : ""}
                </span>
                <button
                  className="btn-add-files"
                  onClick={() => fileInputRef.current.click()}
                >
                  <MdFileUpload size={16} /> Add Files
                </button>
                <button
                  className="btn-add-folder"
                  onClick={() => folderInputRef.current.click()}
                >
                  <MdFolder size={16} /> Add Folder
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*"
                  multiple
                />
                <input
                  type="file"
                  ref={setFolderRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept="image/*"
                  multiple
                />
              </div>
              <button className="btn-clear-all" onClick={clearAll}>
                <MdDelete size={16} /> Clear All
              </button>
            </div>

            <div className="file-list">
              {items.map((item, index) => (
                <div key={index} className={`file-row ${item.status}`}>
                  <img
                    src={item.preview}
                    alt={item.file.name}
                    className="thumb"
                  />
                  <div className="file-details">
                    <span className="file-name">{item.file.name}</span>
                    <div className="size-info">
                      <span>Original: {formatSize(item.file.size)}</span>
                      {item.convertedSize !== null && (
                        <span className="converted-size">
                          → {selectedLabel}: {formatSize(item.convertedSize)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="file-status">
                    {item.status === "pending" && (
                      <span className="badge pending">Pending</span>
                    )}
                    {item.status === "converting" && (
                      <span className="badge converting">Converting…</span>
                    )}
                    {item.status === "done" && (
                      <>
                        <MdCheckCircle className="done-icon" />
                        <button
                          className="btn-download-single"
                          onClick={() => downloadSingle(item)}
                          title="Download"
                        >
                          <MdDownload size={16} />
                        </button>
                      </>
                    )}
                    {item.status === "error" && (
                      <span className="badge error">Failed</span>
                    )}
                  </div>
                  <button
                    className="btn-remove"
                    onClick={() => removeItem(index)}
                    title="Remove"
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div className="actions">
              {hasPending && (
                <button
                  className="btn-convert"
                  onClick={convertAll}
                  disabled={converting}
                >
                  {converting
                    ? "Converting…"
                    : `Convert All to ${selectedLabel}`}
                  {!converting && <MdCompareArrows size={20} />}
                </button>
              )}
              {hasDone && (
                <button className="btn-download-all" onClick={downloadAll}>
                  Download All
                  <MdDownload size={20} />
                </button>
              )}
              {hasDone && (
                <button className="btn-download" onClick={downloadAllAsZip}>
                  Download All as ZIP
                  <MdDownload size={20} />
                </button>
              )}
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default ImageConverter;
